import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { filterMovies, resolveCategory, allYears, allGenres, type SortKey } from "@/utils/movies";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import FilterPanel from "@/pages/category/components/FilterPanel";

const PAGE_SIZE = 12;

const regionOptions = ["全部", "欧美", "中国", "美国", "韩国", "日本", "英国"];
const yearOptions = ["全部", ...allYears];
const genreOptions = ["全部", ...allGenres];

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "popular", label: "热门" },
  { key: "rating", label: "评分" },
];

export default function Category() {
  const { type = "movie" } = useParams();
  const config = useMemo(() => resolveCategory(type), [type]);

  const [contentType, setContentType] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setContentType(config.defaultType === "movie" ? "电影" : config.defaultType === "tv" ? "电视剧" : "全部");
    setRegion(config.defaultRegion || "全部");
    setGenre(config.defaultGenre || "全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
    setFiltersOpen(false);
  }, [config]);

  const typeFilter: "" | "movie" | "tv" =
    contentType === "电影" ? "movie" : contentType === "电视剧" ? "tv" : "";

  const results = useMemo(
    () =>
      filterMovies({
        type: typeFilter,
        region,
        year,
        genre,
        sort,
      }),
    [typeFilter, region, year, genre, sort]
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const update = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  const resetAll = () => {
    setContentType("全部");
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort("latest");
    setPage(1);
  };

  const panelProps = {
    contentType,
    region,
    year,
    genre,
    regionOptions,
    yearOptions,
    genreOptions,
    onContentType: update(setContentType),
    onRegion: update(setRegion),
    onYear: update(setYear),
    onGenre: update(setGenre),
    onReset: resetAll,
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <div className="mb-5">
        <Breadcrumb items={[{ label: "影视分类", to: "/" }, { label: config.title }]} />
        <h1 className="mt-3 text-2xl md:text-3xl font-heading font-bold text-foreground-950">{config.title}</h1>
        <p className="mt-2 text-sm font-body text-foreground-500 max-w-3xl leading-relaxed">{config.description}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
            <FilterPanel {...panelProps} />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="lg:hidden flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-background-300 bg-background-50 text-sm font-label text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-filter-3-line text-base"></i>
                </span>
                筛选
              </button>
              <p className="text-sm font-body text-foreground-500">
                共 <span className="text-primary-600 font-semibold">{results.length}</span> 部作品
              </p>
            </div>

            <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setSort(opt.key);
                    setPage(1);
                  }}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                    sort === opt.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-9">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage(currentPage - 1)}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-lg"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPage(n)}
                      className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-label transition-colors cursor-pointer ${
                        n === currentPage
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 rounded-lg border border-dashed border-background-300 bg-background-50 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500 mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h3 className="text-base font-heading font-semibold text-foreground-900">暂无匹配的影视内容</h3>
              <p className="mt-2 text-sm font-body text-foreground-500">试试更换筛选条件，或重置后重新浏览</p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line text-base"></i>
                </span>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-primary-950/50" onClick={() => setFiltersOpen(false)}></div>
          <div className="absolute top-0 left-0 h-full w-[86%] max-w-xs bg-background-50 shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 h-16 border-b border-background-200">
              <span className="text-base font-heading font-bold text-foreground-950">筛选</span>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-5">
              <FilterPanel {...panelProps} />
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="mt-6 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                查看 {results.length} 个结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}